import { cx } from "@suse-o11y/class-variance";
import { mergeRefs } from "@suse-o11y/merge-refs";
import { msPerFrame } from "@suse-o11y/ms-per-frame";
import { useOn } from "@suse-o11y/use-on";
import { useThrottle } from "@suse-o11y/use-throttle";
import { Tooltip as AntdTooltip } from "antd/es";
import type { TooltipPropsWithTitle } from "antd/es/tooltip";
import clamp from "lodash/clamp";
import get from "lodash/get";
import invoke from "lodash/invoke";
import React from "react";

import styles from "./BaseTooltip.module.css";

export type { TooltipPlacement } from "antd/es/tooltip";

export type TooltipTrigger = "hover" | "focus" | "click" | "contextMenu";

export interface BaseTooltipProps extends Omit<TooltipPropsWithTitle, "trigger"> {
	trigger?: TooltipTrigger | Array<TooltipTrigger>;

	/**
	 * @default true
	 */
	showArrow?: boolean;

	/**
	 * @default false
	 */
	followCursor?: boolean;
}

export const BaseTooltip = React.forwardRef<HTMLElement, BaseTooltipProps>(
	({ showArrow = true, followCursor = false, ...props }, ref) => {
		const overlayClassName = cx(props.overlayClassName, !showArrow && styles.hideArrow);

		if (followCursor) {
			return <BaseTooltipWithFollowingCursor ref={ref} {...props} overlayClassName={overlayClassName} />;
		}

		return <AntdTooltip ref={ref} {...props} overlayClassName={overlayClassName} />;
	},
);

const BaseTooltipWithFollowingCursor = React.forwardRef<HTMLElement, Omit<BaseTooltipProps, "followCursor">>(
	({ align, onVisibleChange, children, ...props }, ref) => {
		const alignOffsetX = align?.offset?.at(0) ?? 0;
		const alignOffsetY = align?.offset?.at(1) ?? 0;

		const mousePositionRef = React.useRef(0);
		const tooltipRef = React.useRef<unknown>(null);
		const [offset, setOffset] = React.useState(0);

		const adjustOffset = useThrottle((clientX: number) => {
			// the AntdTooltip.ref does not specify the type of the ref but it passes the tooltip instance
			// so the only way to reach the popup node is to invoke getElements
			const popup = invoke(tooltipRef.current, "popupRef.current.getElement") as null | HTMLElement;
			const trigger = get(tooltipRef.current, "triggerRef.current") as null | HTMLElement;

			if (popup != null && trigger != null) {
				const { left, width } = trigger.getBoundingClientRect();

				setOffset(clamp(clientX - left, 0, width - popup.clientWidth));
			}
		}, msPerFrame(60));

		useOn("mousemove", (_, { clientX }) => {
			mousePositionRef.current = clientX;
		});

		return (
			<AntdTooltip
				align={{
					...align,
					offset: [alignOffsetX + offset, alignOffsetY],
				}}
				onVisibleChange={(visible) => {
					if (visible) {
						// the adjustments immediately after the tooltip is shown is necessary to prevent jumps
						adjustOffset(mousePositionRef.current);
					}
					onVisibleChange?.(visible);
				}}
				ref={mergeRefs([ref, tooltipRef])}
				{...props}
				// should not destroy the tooltip for nice animations
				destroyTooltipOnHide={false}
			>
				{React.isValidElement(children)
					? React.cloneElement(
							children as React.DetailedReactHTMLElement<React.HTMLAttributes<HTMLElement>, HTMLElement>,
							{
								onMouseMove: (event) => {
									// the tooltip should only follow the mouse when the target is hovered
									// if the tooltips is hovered the following should pause,
									// otherwise it won't be possible to hover it at all
									adjustOffset(event.clientX);

									invoke(children, "props.onMouseMove", event);
								},
							},
						)
					: children}
			</AntdTooltip>
		);
	},
);
